import type { MediaConstraints } from "@cloudflare/realtimekit";

/**
 * What a live asks of the camera and of a shared screen. Without it the SDK falls back to
 * 640 x 480, and the browser gives each camera its best size up to this one.
 */
export const LIVE_MEDIA: MediaConstraints = {
  video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
  screenshare: { width: { max: 3840 }, height: { max: 2160 }, frameRate: { ideal: 15, max: 30 } },
};

/**
 * The camera's three sizes, sent at once so each receiver gets the one it shows: a quarter size for small
 * tiles, half for in between, full 1920 x 1080 for the big one. The names f, h and q are the ones the kit and
 * the SFU use (https://developers.cloudflare.com/realtime/sfu/features/simulcast/, checked 26 Oct 2026).
 * Only the camera is sent this way; a shared screen stays one sharp layer.
 * Why 2.5 Mbps for the full size: a 1080p talking head encoded as VP8 in real time scored 91.6, 92.5 and 93.2
 * VMAF at 2, 2.5 and 3 Mbps (27 Oct 2026), the three looked the same side by side, and a weak line copes
 * better with the lighter step up to full size.
 */
export const LIVE_SIMULCAST: RTCRtpEncodingParameters[] = [
  { rid: "f", scaleResolutionDownBy: 1, maxBitrate: 2_500_000, maxFramerate: 30 },
  { rid: "h", scaleResolutionDownBy: 2, maxBitrate: 800_000 },
  { rid: "q", scaleResolutionDownBy: 4, maxBitrate: 200_000 },
];

/**
 * Whether this device is a phone, for how the camera is sent. A finger as the pointer and a short side of
 * 500 px or less; the screen's own short side does not change when the phone is turned.
 */
export function sendsAsPhone(coarsePointer: boolean, shortSide: number): boolean {
  return coarsePointer && shortSide > 0 && shortSide <= 500;
}

export function sendsAsPhoneNow(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return sendsAsPhone(window.matchMedia("(pointer: coarse)").matches, Math.min(window.screen?.width ?? 0, window.screen?.height ?? 0));
}

/**
 * What the kit is told at init. Whether a camera is simulcast at all is the preset's say
 * (workers/src/realtimekit/setup.ts); on a computer this fixes the three layers the kit would otherwise choose
 * by the camera's width.
 * Why a phone sends one picture: on 27 Oct 2026 a phone (Android 10, Chrome, mobile data) had its upload
 * squeezed 100% of the time, so Chrome sent only the quarter size layer while every viewer starts on the full
 * one, and the host saw no picture for minutes. With simulcastConfig.disable the kit sends one stream (checked in
 * @cloudflare/realtimekit 2.0.3), which lowers its own size and frame rate to fit a weak line.
 */
export function liveOverrides(phone: boolean = sendsAsPhoneNow()): typeof LIVE_OVERRIDES | typeof PHONE_OVERRIDES {
  return phone ? PHONE_OVERRIDES : LIVE_OVERRIDES;
}

/** What a computer is told at init: the three layers. */
export const LIVE_OVERRIDES = { simulcastConfig: { encodings: LIVE_SIMULCAST } };
export const PHONE_OVERRIDES = { simulcastConfig: { disable: true } };
